import { entityType } from 'config/entityType';
import SuperFetch from 'library/helpers/superFetch';
import _ from 'lodash';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { createMapSaga, updateMapSaga } from 'reduxStore/maps/saga';
import { Notification } from 'zComponents/atoms';
import { selectFloorsByFloorID } from './selectors';
import { floorsActions } from './slice';

export function* getFloors() {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Get floors from the current organisation.
		const floors = yield call(() => SuperFetch.get('/floors'));

		if (floors.statusCode === 200) {
			yield put(floorsActions.loadFloorsSuccess({ orgID, data: floors.data }));
		} else {
			throw Error(floors.error.message || floors.error || 'Failed to load floors.');
		}
	} catch (error) {
		yield put(floorsActions.loadFloorsError(error));
	}
}

export function* getFloorMaps({ payload }) {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Get the floorID from the payload
		const { floorID } = payload;

		// Get the map of the selected floor.
		const map = yield call(() => SuperFetch.get(`/floors/${floorID}/map`));

		if (map.statusCode === 200) {
			yield put(floorsActions.loadFloorMapSuccess({ orgID, floorID, data: map.data }));
		} else {
			throw Error(map.error.message || map.error || 'Failed to load map.');
		}
	} catch (error) {
		yield put(floorsActions.loadFloorMapError(error));
	}
}

export function* createFloor({ payload }) {
	try {
		const { data } = payload;

		// 1. Floor data
		const floorData = {
			siteID: data.siteID,
			name: data.name
		};

		// 2. Create the floor
		const createFloorResponse = yield call(() => SuperFetch.post(`/floors`, floorData));

		if (createFloorResponse.statusCode === 200) {
			const floorID = createFloorResponse.data?.floorID;

			if (!floorID) {
				throw Error('Floor created but floorID not returned from server.');
			}

			// 3. Map data
			const mapData = {
				name: data.name,
				dimensionX: data.dimensionX,
				dimensionY: data.dimensionY,
				note: data.note,
				url: data.url,
				entityID: floorID,
				entityTypeID: entityType.FLOOR
			};

			// 4. Check if we need to create a map
			const hasValidMap = mapData.name && mapData.dimensionX && mapData.dimensionY && floorID;

			Notification('success', 'Floor created successfully.');

			if (hasValidMap) {
				try {
					yield call(createMapSaga, mapData);
				} catch (mapError) {
					Notification('error', 'Map creation failed.');
				}
			}

			yield put(floorsActions.createFloorSuccess());
		} else {
			throw Error(createFloorResponse.error?.message || createFloorResponse.error || 'Failed to create floor.');
		}
	} catch (error) {
		yield put(floorsActions.createFloorError(error.message));
		Notification('error', error.message || 'Failed to create floor.');
	}
}

export function* createFloorSuccess() {
	//? The delay was implemented because the floor is created very quickly in the backend.
	//? Without this delay, when we immediately reload the floors, the newly created floor
	//? might not be available yet due to timing issues. This ensures the UI has enough time
	//? to reflect the newly added floor properly.
	yield delay(500);
	yield put(floorsActions.loadFloors());
}

export function* updateFloor({ payload }) {
	try {
		const { floorID, data } = payload;

		const previousData = yield select(selectFloorsByFloorID, floorID);

		// 1. Floor data
		const floorData = {
			name: data.name
		};

		// 2. Map data
		const mapData = {
			name: data.name,
			dimensionX: data.dimensionX,
			dimensionY: data.dimensionY,
			note: data.note,
			url: data.url,
			entityID: floorID,
			entityTypeID: entityType.FLOOR
		};

		// 3. Check if the floor data has changed
		const floorChanged = !_.isEqual(_.pick(data, ['name']), _.pick(previousData, ['name']));

		// 4. If the floor has changed, proceed with the update
		if (floorChanged) {
			const updateSite = yield call(() => SuperFetch.patch(`/floors/${floorID}`, floorData));
			if (updateSite.statusCode !== 200) {
				throw Error(updateSite.error?.message || updateSite.error || 'Failed to update floor.');
			}
		}

		// 5. Check if the map data has changed
		const mapChanged = !_.isEqual(
			_.pick(data, ['dimensionX', 'dimensionY', 'note', 'url']),
			_.pick(previousData, ['dimensionX', 'dimensionY', 'note', 'url'])
		);

		// 6. If the map has changed, proceed with the update or creation
		if (mapChanged) {
			if (data.mapID) {
				yield call(updateMapSaga, data.mapID, mapData);
			} else if (mapData.name && mapData.dimensionX && mapData.dimensionY) {
				yield call(createMapSaga, mapData);
			}
		}

		yield put(floorsActions.updateFloorSuccess());
		Notification('success', 'Floor updated successfully.');
	} catch (error) {
		yield put(floorsActions.updateFloorError(error));
		Notification('error', error.message || 'Failed to update floor.');
	}
}

export function* updateFloorSuccess() {
	// Reload the floors after Updating a floor
	yield put(floorsActions.loadFloors());
}

export function* deleteFloor({ payload }) {
	try {
		const { floorID } = payload;

		// Delete the floor with the new data.
		const deleteFloorData = yield call(() => SuperFetch.delete(`/floors/${floorID}`));

		if (deleteFloorData.statusCode === 200) {
			Notification('success', 'Floor deleted successfully.');
			yield put(floorsActions.deleteFloorSuccess());
		} else {
			throw Error(deleteFloorData.error?.message || deleteFloorData.error || 'Failed to Delete floor.');
		}
	} catch (error) {
		yield put(floorsActions.deleteFloorError(error));
		Notification('error', error.message || 'Failed to delete floor.');
	}
}

export function* deleteFloorSuccess() {
	// Reload the floors after Deleting a floor
	yield put(floorsActions.loadFloors());
}

// The main export for the saga
export default function* floorsSaga() {
	yield takeLatest(floorsActions.loadFloors, getFloors);
	yield takeLatest(floorsActions.loadFloorMap, getFloorMaps);
	yield takeLatest(floorsActions.createFloor, createFloor);
	yield takeLatest(floorsActions.createFloorSuccess, createFloorSuccess);
	yield takeLatest(floorsActions.updateFloor, updateFloor);
	yield takeLatest(floorsActions.updateFloorSuccess, updateFloorSuccess);
	yield takeLatest(floorsActions.deleteFloor, deleteFloor);
	yield takeLatest(floorsActions.deleteFloorSuccess, deleteFloorSuccess);
}
